import { EMPTY_INSTRUCTION_DOCUMENT } from './instructions';
import type {
  SkillDraft,
  SkillDraftField,
  SkillEditorTab,
  SkillSort,
  SkillStatus,
  SkillStatusFilter,
  SkillsFilters,
  SkillsTab,
  SkillsViewMode,
} from './types';

/**
 * Structural constants of the Skills module. No display text here — labels are
 * resolved from i18n by the components, so this file stays language-agnostic
 * (same convention as `agent-setup/channels/catalog.ts`).
 */

/** Route segment of the section, under `assistants/:AssistantID`. */
export const SKILLS_ROUTE_SEGMENT = 'skills';

/** Path of the editor for a new skill, relative to the section. */
export const NEW_SKILL_SEGMENT = 'new';

/** Path of a skill's execution log, relative to that skill. */
export const SKILL_LOGS_SEGMENT = 'logs';

/**
 * Both statuses, in the order the editor's segmented shows them: Draft first,
 * as in the prototype — a skill starts as a draft and is promoted to active, so
 * the control reads left to right like the lifecycle.
 *
 * Not to be confused with `SKILL_STATUS_FILTERS`, where the list puts Active
 * first because there the question is "what is running".
 */
export const SKILL_STATUSES: SkillStatus[] = ['draft', 'active'];

/** Status pills of the toolbar, left to right. */
export const SKILL_STATUS_FILTERS: SkillStatusFilter[] = ['all', 'active', 'draft'];

/** Sort options of the toolbar, in the order the select shows them. */
export const SKILL_SORTS: SkillSort[] = ['recent', 'name', 'status'];

/** The list opens as a dense table; the grid of cards is opt-in. */
export const DEFAULT_VIEW_MODE: SkillsViewMode = 'table';

/** The section opens on the skills themselves, not on their runs. */
export const DEFAULT_TAB: SkillsTab = 'skills';

/** Everything unfiltered, last updated first. */
export const DEFAULT_FILTERS: SkillsFilters = {
  query: '',
  status: 'all',
  sort: 'recent',
};

/** Placeholder rows the skeleton renders while the first fetch resolves. */
export const SKELETON_ROWS = 4;

/* ─── Editor ───────────────────────────────────────────────────────────── */

/**
 * The fields a skill cannot be saved without (PRD Feature 2). Also the order the
 * error names them in, so the message reads like the form.
 */
export const REQUIRED_SKILL_FIELDS: SkillDraftField[] = ['name', 'description', 'intent'];

/** A brand-new skill: empty and in draft until it is saved. */
export const EMPTY_SKILL_DRAFT: SkillDraft = {
  name: '',
  description: '',
  intent: '',
  instructions: EMPTY_INSTRUCTION_DOCUMENT,
  status: 'draft',
  rules: [],
};

/** The editor opens on the instructions, not on the rules. */
export const DEFAULT_EDITOR_TAB: SkillEditorTab = 'instructions';

/** Both tabs of the editor body, in the order the segmented shows them. */
export const SKILL_EDITOR_TABS: SkillEditorTab[] = ['instructions', 'rules'];
